import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/apiService';
import { AlertTriangle, Settings, User } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';

const MaintenanceGuard = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkMaintenanceStatus();
  }, []);

  const checkMaintenanceStatus = async () => {
    try {
      const status = await apiService.getMaintenanceStatus();
      setIsMaintenanceMode(status.is_maintenance);
      setMaintenanceMessage(status.message || 'Site en maintenance. Veuillez réessayer plus tard.');
    } catch (error) {
      console.error('[MaintenanceGuard] Erreur vérification maintenance:', error);
      // En cas d'erreur API, on laisse passer (pas de blocage)
      setIsMaintenanceMode(false);
    } finally {
      setLoading(false);
    }
  };

  // Pendant le chargement, afficher les enfants (évite le blocage)
  if (loading) {
    return children;
  }

  // Si pas en maintenance, afficher normalement
  if (!isMaintenanceMode) {
    return children;
  }

  // Si en maintenance ET utilisateur est admin authentifié, laisser passer
  if (isAuthenticated && user && user.role === 'admin') {
    return children;
  }

  // Si en maintenance et utilisateur non-admin, afficher page de maintenance
  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-rose-100 flex items-center justify-center px-4">
      <div className="max-w-2xl mx-auto">
        <Card className="bg-white/90 backdrop-blur-sm border-2 border-orange-200 shadow-2xl rounded-3xl overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-orange-500 to-amber-500 text-white p-8">
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                <Settings className="w-8 h-8 text-white" />
              </div>
            </div>
            <CardTitle className="text-3xl sm:text-4xl font-bold text-center mb-2">
              🚧 Site en maintenance
            </CardTitle>
            <CardDescription className="text-orange-100 text-lg text-center">
              Nous effectuons des améliorations pour vous offrir une meilleure expérience
            </CardDescription>
          </CardHeader>
          
          <CardContent className="p-8">
            <div className="text-center space-y-6">
              <div className="bg-orange-50 border-2 border-orange-200 rounded-2xl p-6">
                <AlertTriangle className="w-12 h-12 text-orange-500 mx-auto mb-4" />
                <p className="text-gray-700 text-lg leading-relaxed">
                  {maintenanceMessage}
                </p>
              </div>
              
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border-2 border-purple-200 rounded-2xl p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center justify-center">
                  <User className="w-6 h-6 mr-2 text-purple-600" />
                  Vous êtes un administrateur ?
                </h3>
                <p className="text-gray-600 mb-4">
                  Si vous êtes administrateur, vous pouvez toujours accéder au site pendant la maintenance.
                </p>
                <Button 
                  onClick={() => window.location.href = '/connexion'}
                  className="w-full bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white font-semibold py-3 text-lg shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <User className="w-5 h-5 mr-2" />
                  Connexion Administrateur
                </Button>
              </div>
              
              <div className="text-center">
                <p className="text-gray-500 text-sm">
                  Merci pour votre patience. Nous serons de retour très bientôt !
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MaintenanceGuard;